import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import RegisteredLocation from "../../../../components/registered-location/registered-location.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {emptyLocation, OCCURRENCE_REASON} from "../../dispatch/form/utils/dispatch-form-utils.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const getActionTitle = mode => {
    if (mode === "activity") return "Gerar atividade";
    if (mode === "schedule") return "Gerar atividade";
    return "Gerar despacho sem atendimento";
};

const getLastDisplacementAddress = dispatch => {
    const displacement = dispatch?.displacement?.[dispatch.displacement.length - 1];
    return [
        displacement?.destiny,
        displacement?.destinyNeighborhood,
        displacement?.destinyStreet,
        displacement?.destinyNumber,
    ].filter(Boolean).join(" - ");
};

const GarrisonDispatchActionModal = ({payload, onClose, onSuccess}) => {
    const navigate = navigateHook();
    const [nature, setNature] = useState("");
    const [supervisionGarrisonId, setSupervisionGarrisonId] = useState("");
    const [mileage, setMileage] = useState("");
    const [mileageWarning, setMileageWarning] = useState(null);
    const [saveInvalidMileage, setSaveInvalidMileage] = useState(false);
    const [finalObservation, setFinalObservation] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [step, setStep] = useState("main");
    const [createdDispatch, setCreatedDispatch] = useState(null);
    const [complementResult, setComplementResult] = useState(null);
    const [location, setLocation] = useState(emptyLocation);
    const [complementMileage, setComplementMileage] = useState("");
    const [contactName, setContactName] = useState("");
    const [initialObservation, setInitialObservation] = useState("");
    const {configuration, cardInteractions} = useDashboardContext();
    const requested = Boolean(payload);
    const mode = payload?.mode ?? "dispatch";
    const isActivity = mode === "activity" || mode === "schedule";
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId ?? payload?.garrisonId;
    const schedule = payload?.schedule;
    const scheduleActivity = payload?.activity;

    const dispatchQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.garrisonBySetDispatch(garrisonId, "", isActivity),
        queryFn: () => DispatchService.getGarrisonBySetDispatch(garrisonId, null, isActivity),
        enabled: requested && Boolean(garrisonId),
    });
    const isReady = !dispatchQuery.isFetching && (dispatchQuery.isSuccess || dispatchQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;

        setNature(scheduleActivity?.reason ?? "");
        setSupervisionGarrisonId("");
        setMileage("");
        setMileageWarning(null);
        setSaveInvalidMileage(false);
        setFinalObservation("");
        setIsSubmitting(false);
        setStep("main");
        setCreatedDispatch(null);
        setComplementResult(null);
        setLocation({...emptyLocation(), city: configuration.entityCity});
        setComplementMileage("");
        setContactName("");
        setInitialObservation("");
    }, [configuration.entityCity, requested, scheduleActivity?.reason]);

    const result = dispatchQuery.data ?? {};
    const modalGarrison = result.garrison;
    const dispatchToClose = result.dispatch;
    const kmControl = Boolean(modalGarrison?.kmControl);
    const actualKm = modalGarrison?.actualKm;
    const garrisonBySupervision = result.garrisonBySupervision ?? [];
    const isLoading = dispatchQuery.isLoading;
    const hasError = dispatchQuery.isError;
    const title = step === "complement" ? "Sucesso" : getActionTitle(mode);
    const dispatchToCloseAttendanceId = dispatchToClose?.attendance?.id ?? null;
    const complementGarrison = complementResult?.garrison ?? modalGarrison;
    const complementKmControl = Boolean(complementGarrison?.kmControl);
    const complementActualKm = complementGarrison?.actualKm;
    const isFixedLocationNature = nature === OCCURRENCE_REASON.MAINTENANCE || nature === OCCURRENCE_REASON.REFUELLING;

    const natureOptions = useMemo(() => (result.natures ?? []).map(item => ({
        value: item.name ?? item.alias,
        label: item.alias ?? item.name,
    })), [result.natures]);

    const supervisionOptions = useMemo(() => garrisonBySupervision.map(item => ({
        value: item.id,
        label: item.name,
    })), [garrisonBySupervision]);

    const fixedLocationOptions = useMemo(() => (complementResult?.places ?? []).map(item => ({
        value: item.name,
        label: item.name,
    })), [complementResult?.places]);

    const showSupervision = isActivity && nature === OCCURRENCE_REASON.LEADER_SUPERVISION && supervisionOptions.length > 0;
    const shouldValidateMileage = mode === "schedule" && kmControl;
    const dispatchToCloseAddress = getLastDisplacementAddress(dispatchToClose);

    useEffect(() => {
        if (!requested || !configuration.isSuggestKm) return;

        if (shouldValidateMileage && actualKm !== undefined && actualKm !== null) {
            setMileage(String(actualKm));
        }

        if (step === "complement" && complementKmControl && complementActualKm !== undefined && complementActualKm !== null) {
            setComplementMileage(String(complementActualKm));
        }
    }, [actualKm, complementActualKm, complementKmControl, configuration.isSuggestKm, requested, shouldValidateMileage, step]);

    const validateAvailability = () => {
        if ((modalGarrison === null || modalGarrison === undefined || !modalGarrison.status) && !dispatchToClose?.id) {
            projectUtils.swalErrorMessage("Erro", "A guarnição não está disponível para despacho.", "Ok");
            return false;
        }

        return true;
    };

    const validateMileage = async (value, enabled) => {
        if (!enabled || value === "") return true;

        const mileageResult = await DispatchService.checkMileage(garrisonId, value);
        if (!mileageResult?.invalid || saveInvalidMileage) return true;

        setMileageWarning({
            actualKm: mileageResult.actualKm === "" || mileageResult.actualKm === null || mileageResult.actualKm === undefined ? "0" : mileageResult.actualKm,
        });
        return false;
    };

    const handleMainSubmit = async () => {
        if (!validateAvailability()) return;

        if (!nature) {
            projectUtils.swalErrorMessage("", "Selecione uma natureza.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            if (mode === "schedule") {
                const mileageIsValid = await validateMileage(mileage, shouldValidateMileage);
                if (!mileageIsValid) {
                    setIsSubmitting(false);
                    return;
                }

                const response = await DispatchService.dispatchGenerateByGarrisonSchedule({
                    garrisonId,
                    scheduleId: schedule?.id,
                    scheduleItemId: scheduleActivity?.itemId,
                    garrisonBySupervision: showSupervision ? supervisionGarrisonId : null,
                    actualKm: mileage,
                    finalObservation,
                    dispatchToCloseId: dispatchToClose?.id ?? null,
                });

                if (!response || response === "BAD_REQUEST") {
                    projectUtils.swalErrorMessage("Erro", "Não foi possível gerar a atividade.", "Ok");
                    setIsSubmitting(false);
                    return;
                }

                const checkbook = response.numberCheckbook ? ` Talão ${response.numberCheckbook}.` : "";
                onSuccess();
                projectUtils.swalSuccessMessage(
                    "success",
                    "Sucesso",
                    `Atividade gerada com sucesso.${checkbook}`,
                    "Ok",
                    true,
                    "Editar",
                    ({isDismissed}) => {
                        if (isDismissed) {
                            navigate(`${DESPACHO_WEB_URL}/dispatch/form/${response.id}?redir=despacho/dashboard`, true);
                        }
                    },
                    false,
                    false,
                    "#fff",
                    {cancelButton: "swal-edit-button"},
                );
                return;
            }

            const response = await DispatchService.setDispatchByFill({
                garrisonId,
                nature,
                garrisonBySupervision: showSupervision ? supervisionGarrisonId : "",
                dispatchToCloseId: dispatchToClose?.id ?? null,
                finalObservation,
                dispatchToCloseAttendanceId,
            });

            if (!response || response === "BAD_REQUEST" || !response.id) {
                projectUtils.swalErrorMessage("Erro", `Não foi possível ${isActivity ? "gerar a atividade" : "gerar o despacho"}.`, "Ok");
                setIsSubmitting(false);
                return;
            }

            const fillResult = await DispatchService.getGarrisonBySetDispatchFill(garrisonId);
            setCreatedDispatch(response);
            setComplementResult(fillResult);
            cardInteractions.clearSelection();
            setStep("complement");
            setIsSubmitting(false);
        } catch (error) {
            console.error("Erro ao gerar despacho/atividade pelo dashboard:", error);
            const message = error.response?.data ?? `Não foi possível ${isActivity ? "gerar a atividade" : "gerar o despacho"}.`;
            projectUtils.swalErrorMessage("Erro", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const handleComplementSubmit = async () => {
        const hasAnyAddress = location.place || location.neighborhood || location.street || location.crossroads;

        const mileageIsValid = await validateMileage(complementMileage, complementKmControl);
        if (!mileageIsValid) return;

        if (!hasAnyAddress) {
            projectUtils.swalErrorMessage("", "Informe ao menos uma opção de endereço.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await DispatchService.fillDispatch({
                dispatchId: createdDispatch.id,
                city: location.city || "",
                crossroads: location.crossroads || "",
                neighborhood: location.neighborhood || "",
                number: location.number || "",
                latitude: location.latitude || "",
                longitude: location.longitude || "",
                place: location.place || "",
                street: location.street || "",
                referencePoint: location.reference_point || "",
                mileage: complementMileage || "",
                sectors: (location.sectors ?? []).filter(Boolean),
                contactName: contactName || "",
                initialObservation: initialObservation || "",
                dispatchToCloseId: dispatchToClose?.id ?? null,
            });

            if (response === "BAD_REQUEST") {
                projectUtils.swalErrorMessage("Erro", "Não foi possível complementar o endereço do despacho.", "Ok");
                setIsSubmitting(false);
                return;
            }

            closeModal();
            projectUtils.swalSuccessMessage(
                "success",
                "Sucesso",
                "Alteração realizada com sucesso",
                "Ok",
                true,
                "Editar",
                ({isConfirmed, isDismissed}) => {
                    if (isDismissed) {
                        const urlDev = `${DESPACHO_WEB_URL}/dispatch/form/${createdDispatch.id}`;
                        navigate(urlDev, true)
                        onSuccess({dispatchToCloseAttendanceId})
                        return;
                    }

                    if (isConfirmed) onSuccess({dispatchToCloseAttendanceId});
                },
                false,
                false,
                "#fff",
                {cancelButton: "swal-edit-button"},
            );
        } catch (error) {
            console.error("Erro ao complementar endereço do despacho pelo dashboard:", error);
            const message = error.response?.data?.errorMessages ?? "Não foi possível complementar o endereço do despacho.";
            projectUtils.swalErrorMessage("Erro", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const modalWidth = step === "complement" ? "w-8/12" : "w-6/12";

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title={title} className={modalWidth} isCloseClick={false} animated={false}>
                <Modal.Body className="relative max-h-[calc(100vh-180px)] flex flex-col gap-4 overflow-auto">
                    {hasError && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            Não foi possível carregar os dados para gerar o despacho.
                        </div>
                    )}

                    {!hasError && step === "main" && (
                        <>
                            <div>
                                <p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p>
                                <span className="text-sm text-[#555]">{modalGarrison?.name ?? garrison?.garrisonName}</span>
                            </div>

                            {mode === "schedule" && (
                                <div className="flex flex-col">
                                    <p className="text-[14px] font-bold text-[#676a6c]">Atividade programada</p>
                                    <span className="text-sm text-[#555]">{schedule?.name}</span>
                                    <span className="text-sm text-[#555]">{scheduleActivity?.reason}</span>
                                    <span className="text-sm text-[#555]">{scheduleActivity?.placeDescr}</span>
                                </div>
                            )}

                            {mode !== "schedule" && (
                                <SelectComponent
                                    classNameWrapper="w-full"
                                    label="Natureza *"
                                    value={nature}
                                    options={natureOptions}
                                    onChange={(selected) => setNature(selected?.value ?? "")}
                                />
                            )}

                            {showSupervision && (
                                <SelectComponent
                                    classNameWrapper="w-full"
                                    label="Fiscalizar a guarnição"
                                    value={supervisionGarrisonId}
                                    options={supervisionOptions}
                                    onChange={(selected) => setSupervisionGarrisonId(selected?.value ?? "")}
                                />
                            )}

                            {shouldValidateMileage && (
                                <InputForm
                                    id="dashboard-schedule-mileage"
                                    label="Km partida"
                                    integerOnly
                                    type="number"
                                    value={mileage}
                                    min={1}
                                    onChange={({target}) => {
                                        setMileage(target.value);
                                        setMileageWarning(null);
                                        setSaveInvalidMileage(false);
                                    }}
                                />
                            )}

                            {dispatchToClose && (
                                <>
                                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                        <b>O despacho atual será encerrado.</b>
                                        <div>{dispatchToClose.nature}</div>
                                        {dispatchToCloseAddress && <div>{dispatchToCloseAddress}</div>}
                                    </div>

                                    <InputTextarea label="Observação final" value={finalObservation}
                                                   onChange={({target}) => setFinalObservation(target.value)} height={120}/>
                                </>
                            )}

                            {mileageWarning && (
                                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    <div className="mb-2 text-center">
                                        O km informado é menor que o atual. Deseja continuar?<br/>
                                        Km atual: <b>{mileageWarning.actualKm}</b>
                                    </div>
                                    <InputCheckbox
                                        id="dashboard-schedule-save-invalid-mileage"
                                        label="Sim, continuar mesmo assim"
                                        checked={saveInvalidMileage}
                                        onChange={({target}) => setSaveInvalidMileage(target.checked)}
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {!hasError && step === "complement" && (
                        <>
                            <div className="text-center text-lg text-[#555]">
                                {createdDispatch?.start && (
                                    <div><b>Data/Hora inicial:</b> {projectUtils.formatDate(createdDispatch.start)}</div>
                                )}
                                <div><b>N° Talão:</b> {createdDispatch?.numberCheckbook ?? ""}</div>
                            </div>

                            {isFixedLocationNature ? (
                                <SelectComponent
                                    classNameWrapper="w-full"
                                    label="Local pré-cadastrado"
                                    value={location.place}
                                    options={fixedLocationOptions}
                                    onChange={selected => setLocation(prev => ({...prev, place: selected?.value ?? ""}))}
                                />
                            ) : (
                                <RegisteredLocation
                                    showTitle={false}
                                    data={location}
                                    setData={setLocation}
                                    showSectors
                                    storePlaceName
                                    coordinatesRequired={false}
                                />
                            )}

                            {complementKmControl && (
                                <>
                                    <InputForm
                                        id="dashboard-fill-mileage"
                                        label="Km partida"
                                        integerOnly
                                        type="number"
                                        min={1}
                                        value={complementMileage}
                                        onChange={({target}) => {
                                            setComplementMileage(target.value);
                                            setMileageWarning(null);
                                            setSaveInvalidMileage(false);
                                        }}
                                    />

                                    {mileageWarning && (
                                        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                            <div className="mb-2 text-center">
                                                O km informado é menor que o atual. Deseja continuar?<br/>
                                                Km atual: <b>{mileageWarning.actualKm}</b>
                                            </div>
                                            <InputCheckbox
                                                id="dashboard-fill-save-invalid-mileage"
                                                label="Sim, continuar mesmo assim"
                                                checked={saveInvalidMileage}
                                                onChange={({target}) => setSaveInvalidMileage(target.checked)}
                                            />
                                        </div>
                                    )}
                                </>
                            )}

                            <InputForm
                                id="dashboard-fill-contact-name"
                                label="Solicitante"
                                value={contactName}
                                onChange={({target}) => setContactName(target.value)}
                            />

                            <InputTextarea
                                id="dashboard-fill-initial-observation"
                                label="Observação inicial"
                                value={initialObservation}
                                onChange={({target}) => setInitialObservation(target.value)}
                                height={100}
                            />
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {step === "complement" ? (
                        <Button type="button" onClick={handleComplementSubmit} loading={isSubmitting} actionFeedbackType='save'>Salvar</Button>
                    ) : (
                        <Button type="button" onClick={handleMainSubmit} loading={isSubmitting} disabled={hasError || isLoading} actionFeedbackType='save'>Salvar</Button>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GarrisonDispatchActionModal;